import useSWR, { mutate as globalMutate } from 'swr';
import { useMemo, useCallback } from 'react';
import { getEntityFieldValues, saveEntityFieldValues } from '../services/EntityDynamicFieldsServices';

export function getEntityDynamicFieldsCacheKey(entityTypeCode, entityId) {
  return ['entity-dynamic-fields', entityTypeCode, entityId];
}

export function invalidateEntityDynamicFieldsCache(entityTypeCode, entityId) {
  return globalMutate(
    getEntityDynamicFieldsCacheKey(entityTypeCode, entityId),
    undefined,
    { revalidate: true }
  );
}

export default function useEntityDynamicFields(entityTypeCode, entityId) {
  const key = entityTypeCode && entityId ? getEntityDynamicFieldsCacheKey(entityTypeCode, entityId) : null;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getEntityFieldValues(entityTypeCode, entityId);
      if (result?.statusCode === 200) {
        let parsedData = result.data;
        if (typeof parsedData === 'string') {
          try {
            parsedData = JSON.parse(parsedData);
          } catch (e) {
            console.error('Failed to parse entity dynamic fields data JSON string:', e);
            parsedData = null;
          }
        }
        
        // Handle double-escaped sub-JSON string if SQL output is JSON string formatted
        if (parsedData && typeof parsedData.FieldValues === 'string') {
          try {
            parsedData.FieldValues = JSON.parse(parsedData.FieldValues);
          } catch (e) {
            console.error('Failed to parse FieldValues JSON string:', e);
          }
        }
        
        return parsedData;
      }
      throw new Error(result?.message || 'Failed to fetch entity dynamic fields');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const fieldValues = useMemo(() => {
    if (!data?.FieldValues && !data?.fieldValues) return [];
    const rawFields = data.FieldValues || data.fieldValues || [];
    return rawFields.map((field) => ({
      masterFieldId: field.MasterFieldId || field.masterFieldId,
      fieldName: field.FieldName || field.fieldName,
      fieldValue: field.FieldValue || field.fieldValue,
      MasterFieldId: field.MasterFieldId || field.masterFieldId,
      FieldName: field.FieldName || field.fieldName,
      FieldValue: field.FieldValue || field.fieldValue,
    }));
  }, [data]);

  const handleSave = useCallback(async (fields) => {
    if (!entityTypeCode || !entityId) {
      throw new Error('entityTypeCode and entityId are required to save dynamic fields');
    }
    // Fields must be mapped to the database structure: { masterFieldId, fieldValue }
    const mappedFields = fields.map((f) => ({
      masterFieldId: f.masterFieldId || f.MasterFieldId,
      fieldValue: f.fieldValue || f.FieldValue,
    }));

    const payload = {
      entityTypeCode,
      entityId,
      fields: mappedFields,
    };

    const res = await saveEntityFieldValues(payload);
    if (res?.statusCode === 200) {
      await mutate();
      return res;
    }
    throw new Error(res?.message || 'Failed to save entity dynamic fields');
  }, [entityTypeCode, entityId, mutate]);

  const refresh = useCallback(() => mutate(), [mutate]);

  return {
    dynamicFieldsData: data,
    fieldValues,
    isLoading,
    error,
    saveFieldValues: handleSave,
    refresh,
  };
}
